import {
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
} from '@monorepo-types/dc';
import { assign, setup } from 'xstate';

import { actors } from './getOffer.actor';
import { FlowConsumerContextEnergy as FlowContext } from '../../../context';
import type { OfferResponse } from '../../types';

// Shared by every invoke's `onError` below — xstate surfaces a rejected actor's error as `event.error`,
// with the message set by the actor (see getOffer.actor.ts) identifying which failure occurred.
const matchesErrorMessage =
  (message: string) =>
  ({ event }: { event: unknown }) =>
    (event as { error?: { message?: string } })?.error?.message === message;

const getOfferErrorTransitions = [
  { target: 'ERROR_NOT_FOUND', guard: matchesErrorMessage('Not Found') },
  { target: 'ERROR_NOT_FOUND', guard: matchesErrorMessage('Internal error') },
  { target: 'ERROR_NOT_FOUND', guard: matchesErrorMessage('Failed to fetch') },
  { target: 'ERROR_NOT_AVAILABLE', guard: matchesErrorMessage('Sales Cap Reached') },
  { target: 'ERROR_NOT_AUTHENTICATED', guard: matchesErrorMessage('Unauthorized') },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getOfferMachine: any = (fetchedOffers?: OfferResponse) =>
  setup({
    types: {} as {
      input: {
        businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
        label: DC_Repositories_Base_Enumerations_Label;
      };
      context: {
        allowDynamicOrHybridOnly: boolean;
        businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
        label: DC_Repositories_Base_Enumerations_Label;
        errorMessage?: string;
        flowContext: FlowContext;
        offer?: OfferResponse;
      };
      events:
        | { type: 'GET_OFFER'; flowContext: FlowContext; allowDynamicOrHybridOnly?: boolean }
        | { type: 'GET_EXTENSION_OFFER'; flowContext: FlowContext; allowDynamicOrHybridOnly?: boolean }
        | { type: 'CACHE_HIT' };
    },
    actors,
  }).createMachine({
    id: 'getOffer',
    initial: 'IDLE',
    context: ({ input }) => ({
      businessUnit: input.businessUnit,
      label: input.label,
      allowDynamicOrHybridOnly: false,
      flowContext: {} as FlowContext,
      offer: fetchedOffers,
    }),
    states: {
      IDLE: {
        on: {
          GET_OFFER: {
            target: 'FETCHING',
            actions: assign(({ context, event }) => ({
              ...context,
              flowContext: event.flowContext,
              allowDynamicOrHybridOnly: event.allowDynamicOrHybridOnly,
            })),
          },
          GET_EXTENSION_OFFER: {
            target: 'GET_EXTENSION',
            actions: assign(({ context, event }) => ({
              ...context,
              flowContext: event.flowContext,
              allowDynamicOrHybridOnly: event.allowDynamicOrHybridOnly,
            })),
          },
          // Reuse the response seeded from the flow context (via fetchedOffers) without a network call.
          CACHE_HIT: {
            target: 'SUCCESS',
          },
        },
      },
      FETCHING: {
        invoke: {
          src: 'getOffer',
          input: ({ context }) => ({
            businessUnit: context.businessUnit,
            label: context.label,
            values: context.flowContext,
            allowDynamicOrHybridOnly: context.allowDynamicOrHybridOnly,
          }),
          onDone: {
            target: 'SUCCESS',
            actions: assign(({ context, event }) => ({
              ...context,
              offer: event.output,
            })),
          },
          onError: getOfferErrorTransitions,
        },
      },
      GET_EXTENSION: {
        invoke: {
          src: 'getContractExtension',
          input: ({ context }) => ({
            businessUnit: context.businessUnit,
            label: context.label,
            values: context.flowContext,
          }),
          onDone: {
            target: 'SUCCESS',
            actions: assign(({ context, event }) => ({
              ...context,
              offer: event.output,
            })),
          },
          onError: getOfferErrorTransitions,
        },
      },
      SUCCESS: {},
      ERROR: {},
      ERROR_NOT_FOUND: {},
      ERROR_NOT_AVAILABLE: {},
      ERROR_NOT_AUTHENTICATED: {},
    },
  });
